import { Button } from '@/components/base/button';
import { PageHeading } from '@/components/base/page-heading';
import { PaymentGatewayEnum } from '@/monevis-platform/graphql/new';
import { cn } from '@/utils';
import { CheckCircle, CreditCard, Shield, Wallet, Zap } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useIntl } from 'react-intl';

// Professional badge for pricing
function PricingBadge({
  children,
  className = '',
  hasDiscount = false,
}: {
  children: React.ReactNode;
  className?: string;
  hasDiscount?: boolean;
}): JSX.Element {
  return (
    <div
      className={cn(
        'inline-flex items-center gap-2 rounded-xl border px-4 py-3 transition-all duration-200',
        hasDiscount
          ? 'border-emerald-200 bg-emerald-50 shadow-lg shadow-emerald-500/10 dark:border-emerald-800/50 dark:bg-emerald-950/20'
          : 'border-gray-200 bg-gray-50 dark:border-zinc-700 dark:bg-zinc-800',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function OrderSummary({
  total,
  subTotal,
  currency = 'USD',
  onSubmit,
  loading,
}: {
  total: number | undefined;
  subTotal: number | undefined;
  currency: string;
  loading: boolean;
  onSubmit: (method: PaymentGatewayEnum) => void;
}): JSX.Element {
  const intl = useIntl();
  const { t } = useTranslation();

  const totalFormatted = intl.formatNumber(total ?? 0, {
    style: 'currency',
    currency,
  });

  const subTotalFormatted = intl.formatNumber(subTotal ?? 0, {
    style: 'currency',
    currency,
  });

  const hasDiscount = Number(total) < Number(subTotal);

  return (
    <div className="space-y-8">
      {/* Order Total Section */}
      {typeof total === 'number' && (
        <div className="space-y-4 text-center">
          <PageHeading size="xxlarge" className="text-gray-900 dark:text-white">
            {t('orderSummary.totalToPay')}
          </PageHeading>

          <PricingBadge hasDiscount={hasDiscount} className="text-center">
            {hasDiscount && (
              <div className="text-sm font-medium text-red-500 line-through dark:text-red-400">
                {subTotalFormatted}
              </div>
            )}
            <div
              className={cn(
                'text-2xl font-bold',
                hasDiscount
                  ? 'text-emerald-600 dark:text-emerald-400'
                  : 'text-gray-900 dark:text-white',
              )}
            >
              {totalFormatted}
            </div>
            {hasDiscount && (
              <CheckCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
            )}
          </PricingBadge>
        </div>
      )}

      {/* Payment Methods */}
      <div className="space-y-4">
        <h3 className="text-center text-lg font-semibold text-gray-900 dark:text-white">
          {t('orderSummary.selectPaymentMethod')}
        </h3>

        <div className="grid gap-4 md:grid-cols-2">
          {/* Credit Card Payment */}
          <Button
            loading={loading}
            onClick={() => {
              onSubmit(PaymentGatewayEnum.Card);
            }}
            className="h-auto bg-blue-600 p-4 text-white hover:bg-blue-700"
            type="submit"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-white/20 p-2">
                <CreditCard className="h-5 w-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold">
                  {t('orderSummary.payWithCard')}
                </div>
                <div className="text-xs opacity-90">{totalFormatted}</div>
              </div>
            </div>
          </Button>

          {/* Crypto Payment */}
          <Button
            loading={loading}
            onClick={() => {
              onSubmit(PaymentGatewayEnum.Crypto);
            }}
            className="h-auto border-2 border-gray-200 bg-white p-4 text-gray-900 hover:border-gray-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white dark:hover:border-zinc-600"
            variant="outlined"
            type="submit"
          >
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-gray-100 p-2 dark:bg-zinc-800">
                <Wallet className="h-5 w-5" />
              </div>
              <div className="text-left">
                <div className="font-semibold">
                  {t('orderSummary.payWithCrypto')}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {t('orderSummary.bitcoin')} & {t('orderSummary.more')}
                </div>
              </div>
            </div>
          </Button>
        </div>
      </div>

      {/* Trust Indicators */}
      <div className="flex items-center justify-center gap-6 text-xs text-gray-400 dark:text-gray-500">
        <div className="flex items-center gap-1">
          <Shield className="h-3 w-3" />
          <span>{t('orderSummary.securePayment')}</span>
        </div>
        <div className="flex items-center gap-1">
          <Zap className="h-3 w-3" />
          <span>{t('orderSummary.instantActivation')}</span>
        </div>
        <div className="flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          <span>{t('orderSummary.guaranteed')}</span>
        </div>
      </div>
    </div>
  );
}
